import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Camera, Loader2, RefreshCw, Star, Trash2, Wand2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhotoAddButtons, UploadQueue } from "@/components/PhotoUploader";
import { api, apiError } from "@/lib/api";
import { prepareImageForUpload } from "@/lib/image-utils";
import { usePhotoUpload } from "@/hooks/use-photo-upload";

export default function PhotosTab({ vehicle, reload }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get(`/vehicles/${vehicle.id}/photos`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [vehicle.id]); // eslint-disable-line

  // Upload-Queue: Fortschritt pro Bild, Einzel-Retry, HEIC-Unterstützung.
  // Galerie-/Kamera-Uploads landen unter "Weitere" (keine falschen Perspektiven behaupten).
  const uploader = usePhotoUpload(vehicle.id, { onUploaded: () => { load(); reload(); } });

  const setCover = async (imgId) => {
    await api.post(`/vehicles/${vehicle.id}/photos/${imgId}/cover`);
    toast.success("Titelbild gesetzt");
    load();
    reload();
  };

  const remove = async (imgId) => {
    await api.delete(`/vehicles/${vehicle.id}/photos/${imgId}`);
    toast.success("Bild gelöscht");
    load();
    reload();
  };

  const replacePhoto = async (imgId, file) => {
    try {
      const prep = await prepareImageForUpload(file);
      await api.post(`/vehicles/${vehicle.id}/photos/${imgId}/replace`, { data_url: prep.dataUrl, filename: prep.filename });
      toast.success("Bild ersetzt");
      load();
      reload();
    } catch (e) { toast.error(apiError(e, "Ersetzen fehlgeschlagen")); }
  };

  const assignSlot = async (imgId, slotKey) => {
    try {
      await api.patch(`/vehicles/${vehicle.id}/photos/${imgId}/slot`, { slot: slotKey || null });
      load();
      reload();
    } catch (e) { toast.error(apiError(e, "Zuordnung fehlgeschlagen")); }
  };

  // Reihenfolge der "Weiteren Fotos" ändern (ohne Drag&Drop - mobiltauglich)
  const moveFree = async (index, dir) => {
    const free = [...(data.free_images || [])];
    const target = index + dir;
    if (target < 0 || target >= free.length) return;
    [free[index], free[target]] = [free[target], free[index]];
    setData((d) => ({ ...d, free_images: free }));
    try {
      await api.patch(`/vehicles/${vehicle.id}/photos/reorder`, { ordered_ids: free.map((i) => i.id) });
    } catch (e) {
      toast.error(apiError(e, "Reihenfolge konnte nicht gespeichert werden"));
      load();
    }
  };

  if (!data) return <div className="text-sm text-muted-foreground py-8 text-center">Fotos werden geladen...</div>;

  const RECOMMENDED = 12;
  const missing = data.slots.filter((s) => !s.images || s.images.length === 0);
  const emptySlots = data.slots.filter((s) => s.images.length === 0);

  // Datei-Input für Slots & Ersetzen: bewusst OHNE capture -> native Auswahl (Kamera ODER Galerie)
  const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,image/*";

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base font-display flex items-center gap-2">
              <Camera className="h-4 w-4 text-primary" /> Fotos hinzufügen
            </CardTitle>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground tabular-nums" data-testid="photos-count">{data.total} Bilder</span>
              <Button size="sm" className="gap-1.5 font-semibold" onClick={() => navigate(`/app/fotobooth?vehicle=${vehicle.id}`)} data-testid="photos-open-fotobooth">
                <Wand2 className="h-4 w-4" /> Fotobooth öffnen
              </Button>
            </div>
          </div>
          <Progress value={(data.total / RECOMMENDED) * 100} className="h-1.5 mt-2" />
          {data.total > 0 && data.total < RECOMMENDED && (
            <div className="flex items-center gap-2 text-xs text-sky-300 pt-1.5" data-testid="photos-recommendation">
              <Info className="h-3.5 w-3.5 shrink-0" />
              {data.total} Foto{data.total === 1 ? "" : "s"} vorhanden. Für ein optimales Inserat empfehlen wir mindestens {RECOMMENDED}. Sie können trotzdem fortfahren.
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <PhotoAddButtons onFiles={(files) => uploader.addFiles(files, null)} testPrefix="photos-add" />
          <UploadQueue items={uploader.items} onRetry={uploader.retry} onDismiss={uploader.dismiss} testPrefix="photos-queue" />

          {missing.length > 0 && (
            <div data-testid="photos-missing-guide">
              <div className="text-xs font-medium text-foreground/90">
                Diese Perspektiven fehlen noch ({missing.length}) – tippen für Kamera oder Galerie:
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {missing.slice(0, 8).map((slot) => (
                  <label
                    key={slot.key}
                    className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary px-2.5 py-1 text-xs font-medium cursor-pointer hover:bg-primary/20 transition-colors"
                    data-testid={`photos-missing-${slot.key}`}
                  >
                    <input
                      type="file"
                      accept={ACCEPT}
                      className="hidden"
                      onChange={(e) => { if (e.target.files?.[0]) uploader.addFiles([e.target.files[0]], slot.key); e.target.value = ""; }}
                      data-testid={`photos-missing-upload-${slot.key}`}
                    />
                    <Camera className="h-3 w-3" />
                    {slot.label}
                  </label>
                ))}
                {missing.length > 8 && (
                  <span className="text-xs text-muted-foreground self-center" data-testid="photos-missing-more">
                    +{missing.length - 8} weitere im Raster unten
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="photos-slot-grid">
            {data.slots.map((slot) => {
              const img = slot.images[0];
              return (
                <div key={slot.key} className={`relative rounded-xl border overflow-hidden ${img ? "border-border" : "border-dashed border-border bg-secondary/30"}`} data-testid={`photos-slot-${slot.key}`}>
                  {img ? (
                    <>
                      <img src={img.url} alt={slot.label} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                      <div className="absolute top-1.5 left-1.5 flex gap-1">
                        {img.is_cover && <span className="rounded-full bg-primary text-primary-foreground text-[9px] font-bold px-1.5 py-0.5">TITELBILD</span>}
                        {img.processing_status === "processed" && <span className="rounded-full bg-emerald-500/90 text-black text-[9px] font-bold px-1.5 py-0.5">STUDIO</span>}
                      </div>
                      <div className="absolute inset-x-0 bottom-0 bg-black/70 px-2 py-1 flex items-center justify-between">
                        <span className="text-[10px] text-white truncate">{slot.label}</span>
                        <div className="flex gap-0.5">
                          <button title="Als Titelbild" onClick={() => setCover(img.id)} className="p-1 text-white/80 hover:text-amber-300" data-testid={`photo-cover-${slot.key}`}>
                            <Star className="h-3 w-3" />
                          </button>
                          <label title="Bild ersetzen" className="p-1 text-white/80 hover:text-sky-300 cursor-pointer">
                            <input type="file" accept={ACCEPT} className="hidden"
                              onChange={(e) => { if (e.target.files?.[0]) replacePhoto(img.id, e.target.files[0]); e.target.value = ""; }}
                              data-testid={`photo-replace-${slot.key}`} />
                            <RefreshCw className="h-3 w-3" />
                          </label>
                          <button title="Löschen" onClick={() => remove(img.id)} className="p-1 text-white/80 hover:text-red-400" data-testid={`photo-delete-${slot.key}`}>
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <label className="flex flex-col items-center justify-center aspect-[4/3] cursor-pointer hover:bg-secondary/60 transition-colors p-2 text-center">
                      <input type="file" accept={ACCEPT} className="hidden"
                        onChange={(e) => { if (e.target.files?.[0]) uploader.addFiles([e.target.files[0]], slot.key); e.target.value = ""; }}
                        data-testid={`photo-upload-${slot.key}`} />
                      <Camera className="h-5 w-5 text-muted-foreground mb-1" />
                      <span className="text-[11px] text-muted-foreground leading-tight">{slot.label}</span>
                      <span className="text-[9px] text-muted-foreground/60 mt-0.5">Kamera oder Galerie</span>
                    </label>
                  )}
                </div>
              );
            })}
          </div>

          {(data.free_images || []).length > 0 && (
            <div data-testid="photos-free-section">
              <div className="text-xs font-medium text-foreground/90 mb-1.5">
                Weitere Fotos ({data.free_images.length}) – Reihenfolge per Pfeilen ändern, optional einer Perspektive zuordnen:
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {data.free_images.map((img, idx) => (
                  <div key={img.id} className="space-y-1" data-testid="photos-free-image">
                    <div className="relative rounded-xl border border-border overflow-hidden">
                      <img src={img.url} alt="" className="aspect-[4/3] w-full object-cover" loading="lazy" />
                      <div className="absolute top-1.5 left-1.5 flex gap-1">
                        {img.is_cover && <span className="rounded-full bg-primary text-primary-foreground text-[9px] font-bold px-1.5 py-0.5">TITELBILD</span>}
                        {img.processing_status === "processed" && <span className="rounded-full bg-emerald-500/90 text-black text-[9px] font-bold px-1.5 py-0.5">STUDIO</span>}
                      </div>
                      <div className="absolute inset-x-0 bottom-0 bg-black/70 px-1.5 py-1 flex items-center justify-between">
                        <div className="flex gap-0.5">
                          <button title="Nach vorne" disabled={idx === 0} onClick={() => moveFree(idx, -1)}
                            className="p-1 text-white/80 hover:text-white disabled:opacity-30" data-testid="photo-move-left">
                            <ArrowLeft className="h-3 w-3" />
                          </button>
                          <button title="Nach hinten" disabled={idx === data.free_images.length - 1} onClick={() => moveFree(idx, 1)}
                            className="p-1 text-white/80 hover:text-white disabled:opacity-30" data-testid="photo-move-right">
                            <ArrowRight className="h-3 w-3" />
                          </button>
                        </div>
                        <div className="flex gap-0.5">
                          <button title="Als Titelbild" onClick={() => setCover(img.id)} className="p-1 text-white/80 hover:text-amber-300" data-testid="photo-free-cover">
                            <Star className="h-3 w-3" />
                          </button>
                          <label title="Bild ersetzen" className="p-1 text-white/80 hover:text-sky-300 cursor-pointer">
                            <input type="file" accept={ACCEPT} className="hidden"
                              onChange={(e) => { if (e.target.files?.[0]) replacePhoto(img.id, e.target.files[0]); e.target.value = ""; }}
                              data-testid="photo-free-replace" />
                            <RefreshCw className="h-3 w-3" />
                          </label>
                          <button title="Löschen" onClick={() => remove(img.id)} className="p-1 text-white/80 hover:text-red-400" data-testid="photo-free-delete">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                    {emptySlots.length > 0 && (
                      <Select value="" onValueChange={(slotKey) => assignSlot(img.id, slotKey)}>
                        <SelectTrigger className="h-7 text-[10px]" data-testid="photo-free-assign">
                          <SelectValue placeholder="Perspektive zuordnen..." />
                        </SelectTrigger>
                        <SelectContent>
                          {emptySlots.map((s) => (
                            <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
